import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit,
  ChangeDetectorRef,
  ViewChildren,
  QueryList,
  Output,
  EventEmitter,
  OnDestroy,
  ViewContainerRef,
  ViewChild,
  ElementRef,
} from '@angular/core'
import { FormControl, FormGroup } from '@angular/forms'
import { GlobalMessageService, GlobalMessageType } from '@spartacus/core'
import { Observable, Subscription } from 'rxjs'
import { filter, map, startWith, take } from 'rxjs/operators'
import {
  CartItemComponentOptions,
  Item,
  CartItemComponent,
} from '../cart-item/cart-item.component'
import { LaunchDialogService } from '@spartacus/storefront'
import { ShoppingCartService } from '../cart.service'
import { ofType, Actions } from '@ngrx/effects'
import { saveAs } from 'file-saver'
import { formatDate, DatePipe } from '@angular/common'
import { ShoppingCart } from '../cart.model'
import { Router } from '@angular/router'

import {
  ActiveCartFacade,
  ConsignmentEntry,
  MultiCartFacade,
  PromotionLocation,
  SelectiveCartFacade,
} from '@spartacus/cart/base/root'
import { CartActions } from '@spartacus/cart/base/core'
import { SpinnerOverlayService } from '../../../../shared/components/spinner-overlay/spinner-overlay.service'
import {
  ACCESSIBILITY,
  UserRoleService,
} from '../../../../shared/services/user-role.service'
import { DialogResponse, VS_LAUNCH_CALLER } from '../../../config/dialog.config'
import {
  Ecommerce,
  EcommerceItem,
  GTMCartType,
  GTMDataLayer,
} from '../../../../shared/models/googleTagManager.model';
import {
  ItemListTypeEnum,
  GtmEvents,
  StoreTypeEnum,
} from '../../../../shared/enums/gtm.enum';
import { GoogleTagManagerService } from '../../../../shared/services/gtm.service';
import { QuoteCartFacade } from '../../../cart/mini-cart/store/mini-quote-cart.facade';

@Component({
  standalone: false,

  selector: 'vs-cart-item-list',
  templateUrl: './cart-item-list.component.html',
  styleUrls: ['./cart-item-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CartItemListComponent implements OnInit, OnDestroy {
  @ViewChildren('cartEntry') cartEntries: QueryList<CartItemComponent>

  @Output() cartEntryList = new EventEmitter()

  @Input() readonly = false

  @Input() hasHeader = true

  @Input() options: CartItemComponentOptions = {
    isSaveForLater: false,
    optionalBtn: null,
  }

  private _items: Item[] = []
  showToggle = true
  form: FormGroup
  currentDate: Date

  @Input('items')
  // TODO: currently we're getting a new array of items if the cart changes.
  // pretty annoying as it forces a repaint on the screen,
  // which is noticable in the UI.
  set items(items: Item[]) {
    this.resolveItems(items)
    this.createForm()
  }
  get items(): Item[] {
    return this._items
  }

  @Input() promotionLocation: PromotionLocation = PromotionLocation.ActiveCart

  @Input('cartIsLoading') set setLoading(value: boolean) {
    if (!this.readonly) {
      // Whenver the cart is loading, we disable the complete form
      // to avoid any user interaction with the cart.
      value
        ? this.form.disable({ emitEvent: false })
        : this.form.enable({ emitEvent: false })
    }
  }

  @Input()
  userType: string

  @Input('cart')
  set cart(cart: ShoppingCart) {
    this.resolveCart(cart)
  }
  get cart(): ShoppingCart {
    return this._cart
  }

  deleteCartDialog
  deliveryGroupDialog
  cartId = ''
  checkAll = false
  private _cart: ShoppingCart
  selectedItems: Item[] = []
  selectedMultipleItems: any = []
  isShipmentPartial = false
  accessibility = ACCESSIBILITY
  getMultipleCheckAvailability: Subscription
  checkAllList: any[] = []

  @ViewChild('openDeliveryDialog')
  openDeliveryDialog: ElementRef

  constructor(
    protected activeCartFacade: ActiveCartFacade,
    protected selectiveCartFacade: SelectiveCartFacade,
    private launchDialogService: LaunchDialogService,
    private globalMessageService: GlobalMessageService,
    private shoppingCartService: ShoppingCartService,
    private multiCartFacade: MultiCartFacade,
    private actions$: Actions,
    private spinnerOverlayService: SpinnerOverlayService,
    private cRef: ChangeDetectorRef,
    private datepipe: DatePipe,
    private router: Router,
    private changeDetection: ChangeDetectorRef,
    private userRoleService: UserRoleService,
    private vcr: ViewContainerRef,
    private gtmService: GoogleTagManagerService,
    private quoteCartFacade: QuoteCartFacade
  ) {}

  ngOnInit() {
    this.cartId = this.cart.code
    this.shoppingCartService.isShippingTypePartial$.subscribe(
      (res) => {
        this.isShipmentPartial = res
        this.cRef.detectChanges()
      },
      (error) => {
        this.globalMessageService.add(
          error,
          GlobalMessageType.MSG_TYPE_ERROR,
          5000
        )
        window.scrollTo(0, 0)
      }
    )
    setTimeout(() => {
      this.activeCartFacade.getActive().subscribe(
        (activeCart) => {
          this.cartId = activeCart.code

          this.changeDetection.detectChanges()
        },
        (error) => {
          this.globalMessageService.add(
            error,
            GlobalMessageType.MSG_TYPE_ERROR,
            5000
          )
          window.scrollTo(0, 0)
        }
      )
    }, 1000)

    const viewcartItemsData: any[] = [];
    let totalValue = 0;
    this.items.forEach((itemEl, index) => {
      viewcartItemsData.push({
        item_id: itemEl?.product?.code,
        item_name: itemEl?.product?.name,
        index: index,
        item_list_id: ItemListTypeEnum.Cart,
        item_list_name: ItemListTypeEnum.Cart,
        price: itemEl?.totalPrice?.value,
        quantity: itemEl?.quantity,
      });
      totalValue += itemEl?.totalPrice?.value * itemEl?.quantity;
    });
    const viewCartDataLayer: GTMDataLayer = {
      event: GtmEvents.ViewCart,
      ecommerce: {
        currency: this.items[0]?.netPrice.currencyIso,
        value: totalValue,
        items: viewcartItemsData,
      },
      commerceType: GTMCartType.BUY_CART,
      cart_id: this.cartId
    };
    this.gtmService.sendEvent(viewCartDataLayer);
  }

  ngAfterViewInit(): void {
    this.cartEntryList.emit(this.cartEntries)
  }

  /**
   * The items we're getting form the input do not have a consistent model.
   * In case of a `consignmentEntry`, we need to normalize the data from the orderEntry.
   */
  private resolveItems(items: Item[]): void {
    if (items.every((item) => item.hasOwnProperty('orderEntry'))) {
      this._items = items.map((consignmentEntry) => {
        const entry = Object.assign(
          {},
          (consignmentEntry as ConsignmentEntry).orderEntry
        )
        entry.quantity = consignmentEntry.quantity
        return entry
      })
    } else {
      this._items = items
    }
  }

  private resolveCart(cartData) {
    this._cart = Object.assign({}, cartData)
  }

  private createForm(): void {
    this.form = new FormGroup({})
    this._items.forEach((item) => {
      const { code } = item.product
      const group = new FormGroup({
        entryNumber: new FormControl((<any>item).entryNumber),
        quantity: new FormControl(item.quantity, { updateOn: 'blur' }),
      })
      if (!item.updateable || this.readonly) {
        group.disable()
      }
      this.form.addControl(code, group)
    })
  }

  removeEntry(item: Item): void {
    if (this.selectiveCartFacade && this.options.isSaveForLater) {
      this.selectiveCartFacade.removeEntry(item)
    } else {
      this.selectiveCartFacade.removeEntry(item)
    }
    delete this.form.controls[item.product.code]
  }

  getControl(item: Item): Observable<FormGroup> {
    return this.form.controls[item.product.code]?.valueChanges.pipe(
      // tslint:disable-next-line:deprecation
      startWith(null),
      map((value) => {
        if (value && this.selectiveCartFacade && this.options.isSaveForLater) {
          this.selectiveCartFacade.updateEntry(
            value.entryNumber,
            value.quantity
          )
        } else if (value) {
          this.activeCartFacade.updateEntry(value.entryNumber, value.quantity)
        }
      }),
      map(() => <FormGroup>this.form.controls[item.product.code])
    )
  }

  toggleButton() {
    this.showToggle = !this.showToggle
  }

  openDeleteDialog(val) {
    // this.OpenPopUp = !this.OpenPopUp;
    // val === false for multiple deletion of multiple entries & val === true for clear all option
    if (val !== true && this.selectedItems.length <= 0) {
      this.globalMessageService.add(
        { key: 'savedCart.selectAtLeastOneProduct' },
        GlobalMessageType.MSG_TYPE_INFO,
        10000
      )
      window.scroll(0, 0)
      return
    } else {
      const componentdata = {
        userType: this.userType,
        cartId: this.cartId,
        cart: val !== true ? this.cart : null,
      }

      const deleteCartDialog = this.launchDialogService.openDialog(
        VS_LAUNCH_CALLER.DELETE_BUY_CART,
        undefined,
        undefined,
        componentdata
      )
      if (deleteCartDialog) {
        deleteCartDialog.pipe(take(1)).subscribe((value) => {})

        this.launchDialogService.dialogClose
          .pipe(
            filter(
              (dialogResponse: DialogResponse) =>
                dialogResponse?.type == VS_LAUNCH_CALLER.DELETE_BUY_CART
            ),
            take(1)
          )
          .subscribe((value) => {
            if (
              value.reason === 'delete-buy-cart' &&
              val === false &&
              this.selectedItems.length != this.cart.entries.length
            ) {
              this.deleteCartItem()
            } else if (value.reason === 'delete-buy-cart') {
              this.deleteCart()
            }
          })
      }
    }
  }

  deleteCart() {
    const removedItemsData: any[] = [];
    let totalValue = 0;
    this.cart?.entries.forEach((itemEl) => {
      removedItemsData.push({
        item_id: itemEl?.product?.code,
        item_name: itemEl?.product?.name,
        index: itemEl.entryNumber,
        item_list_id: ItemListTypeEnum.Cart,
        item_list_name: ItemListTypeEnum.Cart,
        price: itemEl?.totalPrice?.value,
        quantity: itemEl?.quantity,
      });
      totalValue += itemEl.totalPrice.value;
      const removeFromCartDataLayer: GTMDataLayer = {
        event: GtmEvents.RemoveFromCart,
        ecommerce: {
          currency: itemEl.basePrice.currencyIso,
          value: totalValue,
          items: removedItemsData,
        },
        commerceType: GTMCartType.BUY_CART,
        cart_id: this.cartId
      };
      this.gtmService.sendEvent(removeFromCartDataLayer);
    });
    
    // this.OpenPopUp = false;
    this.spinnerOverlayService.show()
    this.multiCartFacade.deleteCart(this.cartId, this.userType)
    this.actions$
      .pipe(ofType(CartActions.DELETE_CART_SUCCESS))
      .pipe(take(1))
      .subscribe((r) => {
        this.spinnerOverlayService.hide()
        this.globalMessageService.add(
          { key: 'savedCart.removedSuccessfully' },
          GlobalMessageType.MSG_TYPE_CONFIRMATION,
          5000
        )
        window.scrollTo(0, 0)
        sessionStorage.setItem('quoteCartType', '')
        sessionStorage.setItem('isRfqCart', '')
        sessionStorage.setItem('itemNumber', '')
        this.multiCartFacade.createCart({
          userId: this.userType,
          extraData: {
            active: true,
          },
        })
        this.actions$
          .pipe(ofType(CartActions.CREATE_CART_SUCCESS), take(1))
          .subscribe((res: any) => {
            this.multiCartFacade.loadCart({
              userId: this.userType,
              cartId: res.payload.cartId,
              extraData: {
                active: true,
              },
            })
          })
      })

    this.actions$
      .pipe(ofType(CartActions.DELETE_CART_FAIL))
      .pipe(take(1))
      .subscribe(
        (r) => {
          this.spinnerOverlayService.hide()
          this.globalMessageService.add(
            { key: 'savedCart.clearCartFailed' },
            GlobalMessageType.MSG_TYPE_ERROR,
            5000
          )
          window.scrollTo(0, 0)
        },
        (error) => {
          this.spinnerOverlayService.hide()
          this.globalMessageService.add(
            error,
            GlobalMessageType.MSG_TYPE_ERROR,
            5000
          )
          window.scrollTo(0, 0)
        }
      )
  }

  deleteCartItem() {
    let itemsToDelete = []
    this.spinnerOverlayService.show('Loading Cart')
    this.selectedItems.sort((a, b) => (a.entryNumber < b.entryNumber ? 1 : -1))
    this.selectedItems.forEach((element) => {
      itemsToDelete.push(element.entryNumber)
    })

    const removedItemsData: any[] = [];
    let totalValue = 0;
    this.selectedItems.forEach((itemEl) => {
      removedItemsData.push({
        item_id: itemEl?.product?.code,
        item_name: itemEl?.product?.name,
        index: itemEl.entryNumber,
        item_list_id: ItemListTypeEnum.Cart,
        item_list_name: ItemListTypeEnum.Cart,
        price: itemEl?.totalPrice?.value,
        quantity: itemEl?.quantity,
      });
      totalValue += itemEl.totalPrice.value;
      const removeFromCartDataLayer: GTMDataLayer = {
        event: GtmEvents.RemoveFromCart,
        ecommerce: {
          currency: itemEl.basePrice.currencyIso,
          value: totalValue,
          items: removedItemsData,
        },
        commerceType: GTMCartType.BUY_CART,
        cart_id: this.cartId
      };
      this.gtmService.sendEvent(removeFromCartDataLayer);
    });

    this.shoppingCartService
      .deleteMultipleCartEntries(this.userType, this.cartId, itemsToDelete)
      .subscribe(
        (response) => {
          this.activeCartFacade
            .getActive()
            .pipe(take(1))
            .subscribe((activeCart) => {
              this.cartId = activeCart.code
              this.multiCartFacade.loadCart({
                userId: 'current',
                cartId: this.cartId,
                extraData: {
                  active: true,
                },
              })
            })
          this.actions$
            .pipe(ofType(CartActions.LOAD_CART_SUCCESS))
            .pipe(take(1))
            .subscribe(
              (s) => {
                this.selectedItems = []
                this.spinnerOverlayService.hide()
                this.globalMessageService.add(
                  { key: 'savedCart.removedSuccessfully' },
                  GlobalMessageType.MSG_TYPE_CONFIRMATION,
                  5000
                )
                window.scrollTo(0, 0)
              },
              (error) => {
                this.spinnerOverlayService.hide()
                this.globalMessageService.add(
                  error,
                  GlobalMessageType.MSG_TYPE_ERROR,
                  5000
                )
                window.scrollTo(0, 0)
              }
            )
        },
        (error) => {
          this.spinnerOverlayService.hide()
          this.globalMessageService.add(
            { key: 'savedCart.errorOccuredAction' },
            GlobalMessageType.MSG_TYPE_ERROR,
            5000
          )
          window.scrollTo(0, 0)
        }
      )
  }

  checkAvailability() {
    let checkAvailabilityArr: any[] = []

    if (this.selectedMultipleItems.length <= 0 && !this.checkAll) {
      this.globalMessageService.add(
        { key: 'savedCart.selectAtLeastOneProduct' },
        GlobalMessageType.MSG_TYPE_INFO,
        10000
      )
      window.scroll(0, 0)
      return
    } else {
      this.spinnerOverlayService.show('Loading Cart')
      this.selectedMultipleItems.forEach((element) => {
        checkAvailabilityArr.push({
          quantity: element.entry.quantity,
          customerRequestedDate: element.entry.customerRequestedDate,
          entryNumber: element.entry.entryNumber,
          selectedPlant: element.plant,
        })
      })
      this.getMultipleCheckAvailability = this.shoppingCartService
        .getMultipleCheckAvailability(
          checkAvailabilityArr,
          this.cartId,
          this.userType
        )
        .subscribe(
          (res: any) => {
            if (res) {
              this.multiCartFacade.loadCart({
                cartId: res.code,
                userId: this.userType,
                extraData: {
                  active: true,
                },
              })
              this.actions$
                .pipe(ofType(CartActions.LOAD_CART_SUCCESS))
                .pipe(take(1))
                .subscribe(
                  (r) => {
                    this.selectedItems = []
                    this.selectedMultipleItems = []
                    this.checkAllList = []
                    this.checkAll = false
                    this.shoppingCartService.setCheckAvailabilityFlag([], false)
                    this.spinnerOverlayService.hide()
                    this.globalMessageService.add(
                      { key: 'savedCart.cartUpdated' },
                      GlobalMessageType.MSG_TYPE_INFO,
                      10000
                    )
                    window.scrollTo(0, 0)
                  },
                  (error) => {
                    this.spinnerOverlayService.hide()
                    this.globalMessageService.add(
                      error,
                      GlobalMessageType.MSG_TYPE_ERROR,
                      5000
                    )
                    window.scrollTo(0, 0)
                  }
                )
            }
          },
          (error: any) => {
            this.spinnerOverlayService.hide()
            this.globalMessageService.add(
              error,
              GlobalMessageType.MSG_TYPE_ERROR,
              5000
            )
            window.scrollTo(0, 0)
          }
        )
    }
  }
  openSaveCartModal() {
    const componentdata = {
      currentCart: this.cart,
    }

    const saveCartDialog = this.launchDialogService.openDialog(
      VS_LAUNCH_CALLER.CREATE_SAVED_CART,
      undefined,
      undefined,
      componentdata
    )
    if (saveCartDialog) {
      saveCartDialog.pipe(take(1)).subscribe((value) => {})
      this.launchDialogService.dialogClose.subscribe((value) => {})
    }
  }
  downloadCart() {
    if (!this.userType || !this.cartId) {
      return
    }
    this.shoppingCartService.exportCart(this.userType, this.cartId).subscribe(
      (res) => {
        if (res !== null && res !== undefined) {
          const currentDate = this.datepipe.transform(new Date(), 'yyyyMMdd')
          let fileName = this.cartId + currentDate
          const blob = new Blob([res], { type: 'application/vnd.ms.excel' })
          const file = new File([blob], fileName + '.xlsx', {
            type: 'application/vnd.ms.excel',
          })
          saveAs(file)
        } else {
          this.displayDownloadError()
        }
      },
      (error) => {
        this.displayDownloadError()
      }
    )
  }

  displayDownloadError() {
    this.globalMessageService.add(
      { key: 'savedCart.issueDownloadingXlsx' },
      GlobalMessageType.MSG_TYPE_ERROR,
      5000
    )
    window.scrollTo(0, 0)
  }

  onEntryCheck(e) {
    if (e.checked) {
      this.selectedItems.push(this._items[e.entryNumber])
    } else {
      this.selectedItems = this.selectedItems.filter(
        (item) => item.entryNumber !== e.entryNumber
      )
    }
  }
  onMultipleEntryCheck(e) {
    if (this.checkAll) {
      if (this.selectedMultipleItems.length === 0) {
        this.selectedMultipleItems.push(e)
      } else {
        this.selectedMultipleItems = this.checkEntries(
          e,
          this.selectedMultipleItems
        )
      }
    } else if (e.checked) {
      this.selectedMultipleItems = this.selectedMultipleItems.filter(
        (item) => item.entry.entryNumber !== e.entry.entryNumber
      )
      this.selectedMultipleItems.push(e)
    } else {
      this.selectedMultipleItems = this.selectedMultipleItems.filter(
        (item) => item.entry.entryNumber !== e.entry.entryNumber
      )
      if (this.checkAllList.length === 0) {
        this.checkAllList.push(e)
      } else {
        this.checkAllList = this.checkEntries(e, this.checkAllList)
      }
    }
  }
  checkEntries(e, list) {
    list.forEach((element, i) => {
      if (element.entry.entryNumber === e.entry.entryNumber) {
        list[i] = e
      } else {
        if (
          list.findIndex(
            (ele) => ele.entry.entryNumber == e.entry.entryNumber
          ) === -1
        ) {
          list.push(e)
        }
      }
    })
    return list
  }

  checkAllEntries(event: any) {
    const checkValue = event.target.checked
    this.selectedItems = []
    this.selectedMultipleItems = []
    if (checkValue) {
      this.selectedItems = this.shoppingCartService.populateQuoteConvertRequest(
        this._items
      )

      if (this.checkAllList.length !== 0)
        this.selectedMultipleItems = this.checkAllList
    }
  }

  addToQuoteCart() {
    let emptyCart: boolean = false;
    if (this.selectedItems.length <= 0) {
      this.globalMessageService.add(
        { key: 'savedCart.selectAtleastOneProduct' },
        GlobalMessageType.MSG_TYPE_INFO,
        10000
      )
      window.scroll(0, 0)
      return
    }

    emptyCart = (this.selectedItems.length == this.cart.entries.length) ? true : false;

    this.spinnerOverlayService.show()
    this.shoppingCartService
      .convertToQuoteCart(this.userType, this.cartId, this.selectedItems)
      .subscribe(
        (res) => {
          this.multiCartFacade.loadCart({
            userId: this.userType,
            cartId: this.cartId,
            extraData: {
              active: true,
            },
          });

          this.quoteCartFacade.loadCart();

          this.actions$
            .pipe(ofType(CartActions.LOAD_CART_SUCCESS), take(1))
            .subscribe(
              (r) => {
                this.selectedItems = []
                if (!emptyCart) {
                  this.globalMessageService.add(
                    { key: 'savedCart.movedToQuoteCart' },
                    GlobalMessageType.MSG_TYPE_INFO,
                    10000
                  )
                  window.scroll(0, 0)
                  this.spinnerOverlayService.hide()
                } else {
                  this.spinnerOverlayService.hide()
                  this.router.navigate(['/quote/cart'])
                }
              },
              (error) => {
                this.globalMessageService.add(
                  error,
                  GlobalMessageType.MSG_TYPE_ERROR,
                  5000
                )
                window.scrollTo(0, 0)
              }
            )
        },
        (err) => {
          this.selectedItems = []
          this.spinnerOverlayService.hide()
          this.globalMessageService.add(
            { key: 'savedCart.addToquoteCartFailed' },
            GlobalMessageType.MSG_TYPE_ERROR,
            10000
          )
          window.scroll(0, 0)
        }
      )
  }

  openDeliveryGroupsDialog() {
    const componentdata = {
      cart: this.cart,
      items: this.items,
      userType: this.userType,
    }

    const deliveryGroupDialog = this.launchDialogService.openDialog(
      VS_LAUNCH_CALLER.DELIVERY_GROUP,
      this.openDeliveryDialog,
      this.vcr,
      componentdata
    )
    if (deliveryGroupDialog) {
      deliveryGroupDialog.subscribe((value) => {})
      this.launchDialogService.dialogClose
        .pipe(
          filter(
            (dialogResponse: DialogResponse) =>
              dialogResponse?.type == VS_LAUNCH_CALLER.DELIVERY_GROUP
          ),
          take(1)
        )
        .subscribe((value) => {
          if (value?.reason == 'save-delivery-groups') {
            if (Array.isArray(value.data)) {
              this._cart.entries = value.data
              this.spinnerOverlayService.show()
              this.shoppingCartService
                .addDeliveryGroups(this.userType, this.cartId, this._cart)
                .subscribe(
                  (sucess) => {
                    this.multiCartFacade.loadCart({
                      userId: this.userType,
                      cartId: this.cartId,
                      extraData: {
                        active: true,
                      },
                    })
                    this.actions$
                      .pipe(ofType(CartActions.LOAD_CART_SUCCESS), take(1))
                      .subscribe(
                        (success) => {
                          this.spinnerOverlayService.hide()
                        },
                        (error) => {
                          this.spinnerOverlayService.hide()
                          this.globalMessageService.add(
                            error,
                            GlobalMessageType.MSG_TYPE_ERROR,
                            5000
                          )
                          window.scrollTo(0, 0)
                        }
                      )
                  },
                  (error) => {
                    this.globalMessageService.add(
                      error,
                      GlobalMessageType.MSG_TYPE_ERROR,
                      5000
                    )
                    window.scrollTo(0, 0)
                  }
                )
            }
          }
        })
    }
  }

  checkUserEnabled(flagName) {
    return this.userRoleService.checkUserRoleEnabled(flagName)
  }
  ngOnDestroy() {
    if (this.getMultipleCheckAvailability) {
      this.getMultipleCheckAvailability.unsubscribe()
    }
  }
}
